/**
 * ModuleGuard - Route protection for disabled modules.
 *
 * Team module settings live centrally under Laginställningar → Funktioner, so
 * a disabled feature is blocked for everyone on its own route. Team settings
 * remain outside module guards and can always be used to turn features on.
 */

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useModules } from '../context/ModulesContext';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { AlertCircle, Home } from 'lucide-react';
import { tokens } from '../theme/tokens';

const MODULE_NAMES = {
  training: 'Träning',
  match: 'Match',
  calendar: 'Kalender',
  squad: 'Trupp',
  player_development: 'Spelarutveckling',
  chat: 'Chatt',
  tactic: 'Taktik',
  evaluations: 'Utvärderingar',
  ratings: 'Bedömningar',
  individual_training: 'Individuell träning',
  tests: 'Tester',
  statistics: 'Statistik',
  challenges: 'Utmaningar',
  praise: 'Beröm',
  media: 'Media',
  posts: 'Inlägg',
};

function DisabledModuleState({ moduleKey, isCoach, navigate }) {
  const moduleName = MODULE_NAMES[moduleKey] || moduleKey;
  const targetPath = isCoach ? '/oversikt' : '/oversikt';

  return (
    <div
      className="flex items-center justify-center min-h-[60dvh] p-4"
      data-testid="module-disabled-page"
    >
      <Card
        className="max-w-md w-full"
        style={{ backgroundColor: tokens.colors.card }}
      >
        <CardContent className="p-8 text-center">
          <div
            className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center"
            style={{ backgroundColor: `${tokens.colors.accent}20` }}
          >
            <AlertCircle
              className="w-8 h-8"
              style={{ color: tokens.colors.accent }}
            />
          </div>

          <h2
            className="text-xl font-bold mb-2"
            style={{ color: tokens.colors.textPrimary }}
          >
            Funktionen är inte aktiverad
          </h2>

          <p
            className="mb-6"
            style={{ color: tokens.colors.textSecondary }}
          >
            <strong>{moduleName}</strong> är inte aktiverad för det här laget.
            Kontakta en lagadmin om du behöver tillgång.
          </p>

          <Button onClick={() => navigate(targetPath)} className="gap-2">
            <Home className="w-4 h-4" />
            Gå till Översikt
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

export function ModuleGuard({ moduleKey, children }) {
  const navigate = useNavigate();
  const { isModuleEnabled, loading } = useModules();
  const { isCoach } = useAuth();

  if (loading) return null;

  if (isModuleEnabled(moduleKey)) return children;


  return (
    <DisabledModuleState
      moduleKey={moduleKey}
      isCoach={isCoach}
      navigate={navigate}
    />
  );
}

export function RouteModuleGuard({ children }) {
  const location = useLocation();
  const {
    getModuleForRoute,
    isModuleEnabled,
    loading,
  } = useModules();
  const { isCoach } = useAuth();
  const navigate = useNavigate();

  if (loading) return null;

  const moduleKey = getModuleForRoute(location.pathname);
  if (!moduleKey) return children;
  if (isModuleEnabled(moduleKey)) return children;


  return (
    <DisabledModuleState
      moduleKey={moduleKey}
      isCoach={isCoach}
      navigate={navigate}
    />
  );
}

export default ModuleGuard;
